import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Fingerprint, Camera, Upload, ArrowRight, Loader2,
  CheckCircle2, XCircle, AlertTriangle, ScanFace, IdCard, Lock,
} from "lucide-react";
import { api } from "../../lib/api";
import { readDocument, faceDescriptor, faceMatchScore, runLiveness } from "./idVerify";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const STAGES = ["Subject", "Document", "Face + Liveness", "Verdict"];

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function StageDots({ step }) {
  return (
    <div className="flex items-center gap-2" data-testid="kyc-stage-dots">
      {STAGES.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div
            className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${
              i <= step ? "fp-clay-orange text-white" : "bg-white/8 text-white/40 border border-white/10"
            }`}
          >
            {i + 1}
          </div>
          <span className={`hidden text-xs sm:inline ${i <= step ? "text-white" : "text-white/35"}`}>{s}</span>
          {i < STAGES.length - 1 && <div className={`h-px w-6 ${i < step ? "bg-[#F26A21]" : "bg-white/10"}`} />}
        </div>
      ))}
    </div>
  );
}

function Bar({ label, value, danger }) {
  const color = danger && value < 55 ? "bg-red-500" : value >= 70 ? "bg-emerald-500" : "bg-[#F26A21]";
  return (
    <div data-testid={`kyc-bar-${label}`}>
      <div className="mb-1 flex justify-between text-[11px] uppercase tracking-wider text-white/50">
        <span>{label}</span>
        <span className="tabular-nums text-white/80">{Math.round(value)}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-white/10">
        <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${Math.max(2, value)}%` }} />
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-white/5 pb-1">
      <span className="text-white/40">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

const stageAnim = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.4 },
};

export default function Verify() {
  const [step, setStep] = useState(0);
  const [fleetId, setFleetId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [expected, setExpected] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const [docImg, setDocImg] = useState(null);
  const [docResult, setDocResult] = useState(null);
  const [idFaceDesc, setIdFaceDesc] = useState(null);
  const [liveness, setLiveness] = useState(null);
  const [selfieScore, setSelfieScore] = useState(null);
  const [report, setReport] = useState(null);
  const [progress, setProgress] = useState(0);

  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    api.get("/kyc/subjects").then((r) => setSubjects(r.data)).catch(() => {});
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, []);

  async function lockSubject() {
    setError("");
    if (!fleetId.trim() || !driverId.trim()) return setError("Enter both a Fleet ID and a Driver ID.");
    try {
      const r = await api.get("/kyc/subject", { params: { fleet_id: fleetId.trim(), driver_id: driverId.trim() } });
      setExpected(r.data.found ? r.data : null);
      if (!r.data.found) setError("⚠ No matching driver on file for this Fleet ID — verification will flag NO_RECORD.");
      setStep(1);
    } catch {
      setError("Lookup failed.");
    }
  }

  function pickSubject(s) {
    setFleetId(s.fleet_id);
    setDriverId(s.driver_id);
  }

  async function onDocFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
    setDocImg(dataUrl);
    setDocResult(null);
    setIdFaceDesc(null);
  }

  async function analyzeDoc() {
    setError(""); setBusy("Running OCR + MRZ checksum validation on-device…");
    try {
      const img = await loadImage(docImg);
      const res = await readDocument(img);
      setDocResult(res);
      try { const d = await faceDescriptor(img); setIdFaceDesc(d?.descriptor || null); } catch { setIdFaceDesc(null); }
      setBusy("");
    } catch (err) {
      setBusy(""); setError("On-device document engine failed to load. Check the network and retry.");
    }
  }

  async function startCamera() {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640 } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
    } catch {
      setError("Camera access denied. Allow camera to run the liveness check.");
    }
  }

  async function runFaceCheck() {
    if (!videoRef.current || !streamRef.current) return setError("Start the camera first.");
    setError(""); setBusy("Running liveness challenge — blink and slowly turn your head…"); setProgress(0);
    try {
      const live = await runLiveness(videoRef.current, setProgress);
      setLiveness(live);
      let score = 0;
      try {
        const selfie = await faceDescriptor(videoRef.current);
        if (selfie?.descriptor && idFaceDesc) score = faceMatchScore(idFaceDesc, selfie.descriptor);
        else if (!idFaceDesc) setError("No face found on the ID portrait — face-match unavailable, will flag.");
      } catch {}
      setSelfieScore(score);
      setBusy("");
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    } catch {
      setBusy(""); setError("Face engine failed to load.");
    }
  }

  async function submit() {
    setError(""); setBusy("Cross-checking identity against fleet registry…");
    try {
      const payload = {
        fleet_id: fleetId.trim(),
        driver_id: driverId.trim(),
        extracted: docResult?.extracted || {},
        signals: docResult?.signals || {},
        biometrics: {
          face_match_score: selfieScore ?? 0,
          liveness_score: liveness?.score ?? 0,
          liveness_passed: !!liveness?.passed,
        },
      };
      const r = await api.post("/kyc/verify", payload);
      setReport(r.data);
      setStep(3);
      setBusy("");
    } catch {
      setBusy(""); setError("Verification submission failed.");
    }
  }

  const verdictMeta = {
    CLEARED: { cls: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10", Icon: CheckCircle2 },
    REVIEW: { cls: "text-[#F26A21] border-[#F26A21]/40 bg-[#F26A21]/10", Icon: AlertTriangle },
    REJECTED: { cls: "text-red-300 border-red-400/40 bg-red-500/10", Icon: XCircle },
  };

  return (
    <div data-testid="kyc-console" className="relative min-h-[100dvh] overflow-hidden bg-[#0A1B30] text-white">
      <div aria-hidden className="pointer-events-none absolute -top-24 right-0 h-96 w-96 rounded-full bg-[#F26A21]/15 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-20 -left-10 h-96 w-96 rounded-full bg-[#143d6b]/40 blur-3xl" />
      <div aria-hidden className="fp-noise pointer-events-none absolute inset-0 opacity-[0.06]" />

      <div aria-hidden data-testid="kyc-accent-white" className="fixed bottom-2.5 left-0 z-30 h-2.5 w-full bg-white/90" />
      <div aria-hidden data-testid="kyc-accent-line" className="fixed bottom-0 left-0 z-30 h-2.5 w-full bg-[#F26A21] shadow-[0_-2px_16px_rgba(242,106,33,0.55)]" />

      <div className="relative z-10 border-b border-white/10 px-6 py-4">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="fp-clay-orange flex h-10 w-10 items-center justify-center rounded-2xl">
              <Fingerprint className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-display text-lg font-bold tracking-tight">Identity Verification</h1>
              <p className="flex items-center gap-1.5 text-xs text-white/45">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400/80" />
                Global KYC · ICAO-9303 MRZ · on-device biometrics · zero image upload
              </p>
            </div>
          </div>
          <StageDots step={step} />
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-5xl px-6 py-8 pb-16">
        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="kyc-error"
            className="mb-4 rounded-xl border border-red-400/30 bg-red-500/15 px-4 py-2 text-sm text-red-200">{error}</motion.div>
        )}
        {busy && (
          <div data-testid="kyc-busy" className="mb-4 flex items-center gap-3 rounded-xl border border-[#F26A21]/30 bg-[#F26A21]/10 px-4 py-3 text-sm text-orange-100">
            <Loader2 className="h-4 w-4 animate-spin text-[#F26A21]" />
            {busy}{progress > 0 && progress < 100 ? ` ${progress}%` : ""}
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="s1" {...stageAnim} className="grid gap-6 md:grid-cols-2">
              <div className="fp-glass rounded-3xl p-8">
                <h2 className="font-display text-xl font-bold">Subject intake</h2>
                <p className="mb-6 mt-1 text-sm text-white/50">Identify the driver we are verifying against the fleet registry.</p>
                <div className="space-y-2">
                  <Label className="text-white/70">Fleet ID</Label>
                  <div className="relative">
                    <ShieldCheck className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                    <Input data-testid="kyc-fleet-input" value={fleetId} onChange={(e) => setFleetId(e.target.value)} placeholder="FL0333" className="fp-input pl-10" />
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <Label className="text-white/70">Driver ID</Label>
                  <div className="relative">
                    <IdCard className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                    <Input data-testid="kyc-driver-input" value={driverId} onChange={(e) => setDriverId(e.target.value)} placeholder="DR0003" className="fp-input pl-10" />
                  </div>
                </div>
                <Button data-testid="kyc-lock-subject" onClick={lockSubject} className="fp-clay-orange group mt-6 h-12 w-full rounded-2xl font-bold text-white">
                  <span className="flex items-center gap-2">Lock subject <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                </Button>
              </div>
              <div className="fp-glass rounded-3xl p-8">
                <h2 className="font-display text-xl font-bold">Fleet registry</h2>
                <p className="mb-4 mt-1 text-sm text-white/50">Tap a driver to auto-fill.</p>
                <div className="max-h-80 space-y-2 overflow-auto pr-1">
                  {subjects.map((s) => (
                    <button key={s.driver_id} data-testid={`kyc-subject-${s.driver_id}`} onClick={() => pickSubject(s)}
                      className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-left text-sm transition hover:border-[#F26A21]/50 hover:bg-white/[0.07]">
                      <span><b>{s.full_name}</b> <span className="text-white/40">· {s.driver_id}</span></span>
                      <span className="font-mono text-[11px] text-white/40">{s.fleet_id} · {s.doc_type}</span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="s2" {...stageAnim} className="grid gap-6 md:grid-cols-2">
              <div className="fp-glass rounded-3xl p-8">
                <h2 className="font-display text-xl font-bold">Document capture</h2>
                <p className="mb-5 mt-1 flex items-center gap-1.5 text-xs text-emerald-300/80"><Lock className="h-3.5 w-3.5" /> Processed on this device only. No image is uploaded.</p>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onDocFile} className="hidden" data-testid="kyc-doc-file" />
                <div className="flex gap-2">
                  <Button onClick={() => fileRef.current?.click()} data-testid="kyc-upload-doc" className="fp-clay-glass h-12 flex-1 rounded-2xl text-white hover:!text-white">
                    <span className="flex items-center gap-2"><Upload className="h-4 w-4" /> Upload / take photo</span>
                  </Button>
                  {docImg && (
                    <Button onClick={analyzeDoc} data-testid="kyc-analyze-doc" className="fp-clay-orange h-12 flex-1 rounded-2xl font-bold text-white">
                      <span className="flex items-center gap-2"><ScanFace className="h-4 w-4" /> Analyze</span>
                    </Button>
                  )}
                </div>
                {docImg && <img src={docImg} alt="ID preview" className="mt-4 max-h-52 w-full rounded-2xl border border-white/10 object-contain" data-testid="kyc-doc-preview" />}
              </div>
              <div className="fp-glass rounded-3xl p-8">
                <h2 className="font-display text-xl font-bold">Extraction & integrity</h2>
                {!docResult && <p className="mt-3 text-sm text-white/40">Awaiting document analysis…</p>}
                {docResult && (
                  <div className="mt-4 space-y-3 text-sm" data-testid="kyc-doc-result">
                    <Row k="Type" v={docResult.extracted.doc_type} />
                    <Row k="Name" v={`${docResult.extracted.given_names || ""} ${docResult.extracted.surname || ""}`.trim() || "—"} />
                    <Row k="Doc number" v={docResult.extracted.doc_number || "—"} />
                    <Row k="Nationality" v={docResult.extracted.nationality || "—"} />
                    <Row k="Expiry" v={docResult.extracted.expiry || "—"} />
                    <div className={`mt-2 rounded-xl border px-3 py-2 text-xs ${docResult.signals.mrz_valid ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300" : docResult.signals.has_mrz ? "border-red-400/40 bg-red-500/10 text-red-300" : "border-[#F26A21]/40 bg-[#F26A21]/10 text-orange-200"}`}>
                      {docResult.signals.has_mrz ? (docResult.signals.mrz_valid ? "✓ MRZ check digits VALID (ICAO 9303)" : "✗ MRZ CHECKSUM FAILED — data inconsistent") : "⚠ No MRZ detected — OCR-only, reduced assurance"}
                      <span className="ml-2 text-white/40">OCR conf {docResult.signals.ocr_confidence}%</span>
                    </div>
                    <Button onClick={() => setStep(2)} data-testid="kyc-to-face" className="fp-clay-orange group mt-2 h-12 w-full rounded-2xl font-bold text-white">
                      <span className="flex items-center gap-2">Continue to face + liveness <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s3" {...stageAnim} className="grid gap-6 md:grid-cols-2">
              <div className="fp-glass rounded-3xl p-8">
                <h2 className="font-display text-xl font-bold">Liveness & face match</h2>
                <p className="mb-4 mt-1 flex items-center gap-1.5 text-xs text-emerald-300/80"><Lock className="h-3.5 w-3.5" /> Selfie compared to the ID portrait on-device. Nothing leaves the browser.</p>
                <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black">
                  <video ref={videoRef} playsInline muted className="h-64 w-full object-cover" data-testid="kyc-video" />
                  {busy && progress > 0 && <div className="absolute inset-x-0 bottom-0 h-1 bg-[#F26A21] transition-all" style={{ width: `${progress}%` }} />}
                </div>
                <div className="mt-3 flex gap-2">
                  <Button onClick={startCamera} data-testid="kyc-start-camera" className="fp-clay-glass h-12 flex-1 rounded-2xl text-white hover:!text-white">
                    <span className="flex items-center gap-2"><Camera className="h-4 w-4" /> Start camera</span>
                  </Button>
                  <Button onClick={runFaceCheck} data-testid="kyc-run-face" className="fp-clay-orange h-12 flex-1 rounded-2xl font-bold text-white">
                    <span className="flex items-center gap-2"><ScanFace className="h-4 w-4" /> Run check</span>
                  </Button>
                </div>
              </div>
              <div className="fp-glass rounded-3xl p-8">
                <h2 className="mb-4 font-display text-xl font-bold">Biometric signals</h2>
                <div className="space-y-4">
                  <Bar label="face match" value={selfieScore ?? 0} danger />
                  <Bar label="liveness" value={liveness?.score ?? 0} danger />
                  {liveness && <p className="text-xs text-white/50" data-testid="kyc-liveness-detail">Blink {liveness.blink ? "✓" : "—"} · head-move {liveness.movement}px · face in {liveness.frames} frames</p>}
                </div>
                {(selfieScore !== null || liveness) && (
                  <Button onClick={submit} data-testid="kyc-submit" className="fp-clay-orange group mt-6 h-12 w-full rounded-2xl font-bold text-white">
                    <span className="flex items-center gap-2">Run final verdict <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                  </Button>
                )}
              </div>
            </motion.div>
          )}

          {step === 3 && report && (
            <motion.div key="s4" {...stageAnim} data-testid="kyc-verdict" className="space-y-6">
              {(() => { const m = verdictMeta[report.verdict]; const Icon = m.Icon; return (
                <div className={`fp-glass flex flex-col items-center justify-center rounded-3xl border p-10 ${m.cls}`}>
                  <Icon className="mb-3 h-12 w-12" />
                  <p className="text-xs uppercase tracking-[0.3em] opacity-70">Verdict</p>
                  <p data-testid="kyc-verdict-label" className="my-1 font-display text-5xl font-black">{report.verdict}</p>
                  <p className="text-sm opacity-80">Risk score <b className="tabular-nums">{report.score}/100</b></p>
                </div>
              ); })()}
              <div className="grid gap-6 md:grid-cols-2">
                <div className="fp-glass rounded-3xl p-8">
                  <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-white/60">Signal breakdown</h3>
                  <div className="space-y-4">
                    <Bar label="document (35)" value={(report.breakdown.document / 35) * 100} />
                    <Bar label="identity (35)" value={(report.breakdown.identity / 35) * 100} />
                    <Bar label="biometrics (30)" value={(report.breakdown.biometrics / 30) * 100} />
                  </div>
                  {report.matched_record && (
                    <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.04] p-3 text-xs text-white/60" data-testid="kyc-matched-record">
                      <b className="text-white/80">On file:</b> {report.matched_record.full_name} · {report.matched_record.licence_masked} · exp {report.matched_record.expiry} · {report.matched_record.status}
                    </div>
                  )}
                </div>
                <div className="fp-glass rounded-3xl p-8">
                  <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-white/60">Flags ({report.flags.length})</h3>
                  <div className="space-y-2">
                    {report.flags.length === 0 && <p className="text-sm text-emerald-300">No adverse signals.</p>}
                    {report.flags.map((f, i) => (
                      <div key={i} data-testid={`kyc-flag-${f.code}`} className={`rounded-xl border px-3 py-2 text-xs ${f.level === "hard" ? "border-red-400/40 bg-red-500/10 text-red-300" : f.level === "warn" ? "border-[#F26A21]/40 bg-[#F26A21]/10 text-orange-200" : "border-white/10 bg-white/5 text-white/60"}`}>
                        <b>{f.code}</b> — {f.msg}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <Button onClick={() => window.location.reload()} data-testid="kyc-restart" className="fp-clay-glass mx-auto block h-11 rounded-2xl px-6 text-sm text-white hover:!text-white">Run another check</Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
